import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { API_BASE, authHeaders, handleResponse } from "../api/api";
import { getVisitorId } from "../utils/consent";
import { setAnalyticsUser } from "../utils/analytics";
import { useConsent } from "./ConsentContext";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const { adoptFromServer } = useConsent();
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("hr_token"));
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async (tokenValue) => {
    const response = await fetch(`${API_BASE}/users/me`, {
      headers: {
        "Content-Type": "application/json",
        ...authHeaders(tokenValue),
      },
    });
    return handleResponse(response);
  }, []);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    let cancelled = false;

    const initialize = async () => {
      const MAX_ATTEMPTS = 3;
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          const currentUser = await fetchUser(token);
          if (!cancelled) setUser(currentUser);
          break;
        } catch (error) {
          // Only treat an actual 401 (invalid/expired token) as a logout.
          if (error.status === 401) {
            if (!cancelled) {
              localStorage.removeItem("hr_token");
              setToken(null);
              setUser(null);
            }
            break;
          }
          // Network errors, server hiccups, etc. should not destroy the
          // session — retry with backoff before giving up. Without this,
          // a single transient failure (e.g. backend cold-starting right
          // after a page refresh) would leave `user` null while `loading`
          // flips to false, and every page guard reads that as "logged
          // out" and bounces to /login — even though the token is still
          // valid and was never cleared.
          console.error(`Auth initialization attempt ${attempt} failed:`, error);
          if (attempt < MAX_ATTEMPTS) {
            await new Promise((r) => setTimeout(r, attempt * 400));
          }
        }
      }
      if (!cancelled) setLoading(false);
    };

    initialize();
    return () => { cancelled = true; };
  }, [token, fetchUser]);

  // Keep the account and this browser's pseudonymous ID tied together.
  // Runs on every user change — including session restore on page load, not
  // just interactive login — because a returning shopper who never re-enters a
  // password still needs their history joined on this device.
  useEffect(() => {
    setAnalyticsUser(user?.id ?? null);
    if (!user || !token) return;
    const visitorId = getVisitorId();
    if (!visitorId) return;   // localStorage blocked — nothing to link
    fetch(`${API_BASE}/identity/link`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders(token) },
      body: JSON.stringify({ visitor_id: visitorId }),
    })
      .then((r) => (r.ok ? r.json() : null))
      // Sync any consent the server resolved for this device (its own record,
      // or one just adopted from another device) into local state — this is
      // what takes the cookie banner down on a new device the instant sign-in
      // resolves, without a second, separately-racing /consent fetch.
      .then((data) => { if (data?.consent) adoptFromServer(data.consent); })
      .catch(() => {});   // best-effort: a failed link costs personalisation, never the page
  }, [user, token, adoptFromServer]);

  const login = useCallback(
    async (email, password) => {
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      // Surface the unverified-email signal before handleResponse throws a generic error
      if (response.status === 403) {
        const body = await response.json().catch(() => ({}));
        if (body.detail === "email_not_verified") throw new Error("email_not_verified");
      }

      const data = await handleResponse(response);
      localStorage.setItem("hr_token", data.access_token);
      setToken(data.access_token);
      const currentUser = await fetchUser(data.access_token);
      setUser(currentUser);
      window.dispatchEvent(new Event("hr:auth-login"));
      return currentUser;
    },
    [fetchUser]
  );

  const register = useCallback(
    // date_of_birth is "YYYY-MM-DD" (Register.jsx, checkDateOfBirth). It
    // replaced age_range on 2026-10-13; the server sets age_range from it.
    async (name, email, password, date_of_birth, sex = null, phone_number = null) => {
      const response = await fetch(`${API_BASE}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, password, date_of_birth, sex, phone_number }),
      });

      // Return the created user — do NOT auto-login.
      // Email must be verified before the first login is possible.
      return handleResponse(response);
    },
    []
  );

  const logout = useCallback(() => {
    localStorage.removeItem("hr_token");
    setToken(null);
    setUser(null);
  }, []);

  const authFetch = useCallback(
    async (path, options = {}) => {
      const response = await fetch(`${API_BASE}${path}`, {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(token),
          ...(options.headers || {}),
        },
        method: options.method || "GET",
        body: options.body ? JSON.stringify(options.body) : undefined,
      });
      return handleResponse(response);
    },
    [token]
  );

  // Silently renew the token 2 minutes before the 15-minute expiry so
  // long checkout flows never hit a mid-order 401.
  useEffect(() => {
    if (!token || !user) return;
    const timer = setTimeout(async () => {
      try {
        const response = await fetch(`${API_BASE}/auth/refresh`, {
          method: "POST",
          headers: authHeaders(token),
        });
        const data = await handleResponse(response);
        localStorage.setItem("hr_token", data.access_token);
        setToken(data.access_token);
      } catch {
        // Genuinely expired — user will be prompted to re-login on next request
      }
    }, 13 * 60 * 1000);
    return () => clearTimeout(timer);
  }, [token, user]);

  const refreshUser = useCallback(async () => {
    if (!token) return;
    try {
      const currentUser = await fetchUser(token);
      setUser(currentUser);
    } catch {
      // token expired or invalid — stay logged out
    }
  }, [token, fetchUser]);

  // Issues a fresh token with a renewed expiry (does NOT refetch the user
  // object — see refreshUser for that). Call this at points where a long
  // task is about to start (e.g. entering checkout) so the session doesn't
  // expire mid-task. Silently no-ops if the current token is already
  // expired/invalid — that's a real logout, not something to paper over.
  const extendSession = useCallback(async () => {
    if (!token) return null;
    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: "POST",
        headers: authHeaders(token),
      });
      const data = await handleResponse(response);
      localStorage.setItem("hr_token", data.access_token);
      setToken(data.access_token);
      return data.access_token;
    } catch {
      return null;
    }
  }, [token]);

  const value = useMemo(
    () => ({ user, token, loading, login, register, logout, authFetch, refreshUser, extendSession }),
    [user, token, loading, login, register, logout, authFetch, refreshUser, extendSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
